export interface CapitalGoalMonth {
  readonly period: string;
  readonly ingoingsCents: bigint | null;
  readonly outgoingsCents: bigint | null;
}

export interface CapitalGoalProgressMonth {
  readonly period: string;
  readonly status: 'observed' | 'incomplete' | 'future';
  readonly flowCents: bigint | null;
  readonly cumulativeCents: bigint | null;
  readonly remainingCents: bigint | null;
  readonly progressBasisPoints: bigint | null;
  readonly direction: 'forward' | 'backward' | 'stable' | 'unknown';
  readonly goalReached: boolean;
}

/** Objectif d'épargne de trésorerie annuel demandé en recette le 21 octobre.
 * Le cumul repart de zéro au début de l'année de suivi : ce zéro est une
 * origine de progression, JAMAIS un solde bancaire. Les sorties comprennent
 * déjà la rémunération ; elle ne doit pas être retirée une seconde fois.
 * Une lacune interdit le cumul suivant, même si son flux mensuel est connu.
 */
export class WorkingCapitalGoalProgress {
  public constructor(private readonly months: readonly CapitalGoalMonth[]) {}

  public year(
    year: number,
    observedThrough: string,
    annualGoal: bigint | null,
  ): readonly CapitalGoalProgressMonth[] {
    if (
      !Number.isInteger(year) ||
      year < 2020 ||
      year > 2099 ||
      !/^20\d{2}-(0[1-9]|1[0-2])$/u.test(observedThrough)
    )
      throw new Error('Période de progression invalide.');
    const target = annualGoal !== null && annualGoal > 0n ? annualGoal : null;
    let cumulative: bigint | null = 0n;
    return Array.from({ length: 12 }, (_, index) => {
      const period = `${year}-${String(index + 1).padStart(2, '0')}`;
      const future = period > observedThrough;
      const beforeActivity = period < '2020-09';
      const matches = this.months.filter((month) => month.period === period);
      const row = matches.length === 1 ? matches[0] : undefined;
      const flow =
        !future &&
        !beforeActivity &&
        row?.ingoingsCents != null &&
        row.outgoingsCents !== null &&
        row.ingoingsCents >= 0n &&
        row.outgoingsCents >= 0n
          ? row.ingoingsCents - row.outgoingsCents
          : null;
      if (!beforeActivity && !future)
        cumulative = cumulative === null || flow === null ? null : cumulative + flow;
      const value = future || beforeActivity ? null : cumulative;
      return {
        period,
        status: future ? 'future' : flow === null ? 'incomplete' : 'observed',
        flowCents: flow,
        cumulativeCents: value,
        remainingCents:
          target === null || value === null ? null : value >= target ? 0n : target - value,
        // Arrondi symétrique au centième de pourcentage, comme la répartition
        // des dépenses. La troncature sous-estimait certains résultats.
        progressBasisPoints:
          target === null || value === null
            ? null
            : (value * 10000n + (value < 0n ? -target : target) / 2n) / target,
        direction:
          target === null || value === null || flow === null
            ? 'unknown'
            : flow > 0n
              ? 'forward'
              : flow < 0n
                ? 'backward'
                : 'stable',
        goalReached: target !== null && value !== null && value >= target,
      };
    });
  }
}
